import { memo, useEffect } from 'react';
import styled from 'styled-components';
import { applySX, Show, type WithSX } from '@elfsight/lib-utils';
import { Button as LibButton } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { applyTemporaryStyles, useIsOverflowing } from '../../utils';
import { useElements } from '../elements-context';
import { ElementBase } from './element-base';

interface ButtonsProps {
  horizontalPadding: number | [left: number, right: number];
  withScroll?: boolean;
  className?: string;
  rtl?: boolean;
}

export const Buttons = memo(
  ({ className, horizontalPadding, withScroll = true, rtl }: ButtonsProps) => {
    const { editMode } = useSettings();
    const { buttons } = useElements();
    const withMultipleButtons = buttons.length > 1;
    const derivedWithScroll = withMultipleButtons && withScroll;

    const { ref, isOverflowing, revalidate } = useIsOverflowing({
      checkIfOverflowing: (node) => {
        const restoreStyles = applyTemporaryStyles(node, {
          justifyContent: 'flex-start'
        });
        const isOverflowing = node.scrollWidth > node.clientWidth;
        restoreStyles();

        return isOverflowing;
      },
      enabled: derivedWithScroll
    });
    const [leftPadding, rightPadding] = Array.isArray(horizontalPadding)
      ? horizontalPadding
      : [horizontalPadding, horizontalPadding];
    const overflowWidth = `calc(100% + ${rightPadding}px + ${leftPadding}px)`;
    const overflowStyles = {
      width: overflowWidth,
      maxWidth: overflowWidth,
      minWidth: overflowWidth,
      margin: `0 -${rightPadding}px 0 -${leftPadding}px`,
      padding: `0 ${rightPadding}px 0 ${leftPadding}px`,
      direction: rtl ? 'rtl' : undefined,
      justifyContent: 'flex-start',
      overflow: 'auto'
    } as const;

    useEffect(() => {
      if (derivedWithScroll && editMode) {
        revalidate();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buttons.length, derivedWithScroll]);

    return (
      <Show when={!!buttons.length}>
        <Container
          ref={ref}
          className={className}
          $sx={{
            // Ставим через sx, чтобы можно было переопределить через overflowStyles
            justifyContent: 'inherit',
            maxWidth: '100%',
            overflow: withMultipleButtons ? 'visible' : undefined,
            ...(isOverflowing && overflowStyles)
          }}
        >
          {buttons.map(({ id, text, onClick, isPrimary }) => (
            <Button
              key={id}
              onClick={onClick}
              text={text}
              $isPrimary={isPrimary}
            />
          ))}
        </Container>
      </Show>
    );
  }
);

const Container = styled(ElementBase).attrs<WithSX>({
  className: createReadableClass({
    component: 'element-buttons',
    element: 'container'
  })
})`
  ${applySX};
  display: flex;
  gap: 12px;
  scrollbar-width: none;
  align-items: inherit;

  &::-webkit-scrollbar {
    display: none;
  }
`;

interface ButtonProps {
  $isPrimary: boolean;
}

const Button = styled(LibButton).attrs<ButtonProps>(
  ({ $isPrimary, theme: { primaryButton, secondaryButton } }) => ({
    className: createReadableClass({
      component: 'element-buttons',
      element: 'button',
      index: $isPrimary ? 'primary' : 'secondary'
    }),
    ...($isPrimary ? primaryButton : secondaryButton)
  })
)``;
